/**
 * AutoCount API client core.
 *
 * Thin typed wrapper over fetch:
 * - prefixes requests with `${API_BASE_URL}/api/v1`
 * - attaches the bearer token stored by the auth context
 * - parses JSON and normalizes failures into ApiError
 */

import { API_BASE_URL } from "config";

export class ApiError extends Error {
  statusCode: number;

  errorCode?: string;

  constructor(message: string, statusCode: number, errorCode?: string) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }
}

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

/** Serialize defined params into a query string ("" when nothing to send). */
export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
      ...options,
      headers,
    });
  } catch (error) {
    throw new ApiError(
      "Network error: could not reach the AutoCount API",
      0,
      "NETWORK_ERROR"
    );
  }

  let body: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const errorBody = body as { message?: string; errorCode?: string } | null;
    throw new ApiError(
      errorBody?.message || `Request failed with status ${response.status}`,
      response.status,
      errorBody?.errorCode
    );
  }

  return body as T;
}

export function apiGet<T>(path: string, params?: QueryParams): Promise<T> {
  return apiFetch<T>(`${path}${buildQuery(params)}`);
}

export function apiPost<T>(path: string, payload?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
}
